import { animate, AnimationEvent, keyframes, style, transition, trigger, useAnimation } from '@angular/animations';
import { AfterViewChecked, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { CalloutType, FeatureCallout } from '../feature-callout/feature-callout.model';
import { FeatureCalloutService } from '../feature-callout/feature-callout.service';
import { InstallConfigService } from '../install/install-config.service';
import { collapse, expand } from '../shared/animations/animations';
import { LinkTarget } from '../shared/constants/link-target.enum';
import { OfficeRoute } from '../shared/constants/office-route.enum';
import { DropdownMenu } from '../shared/directives/dropdown-menu/dropdown-menu.component';
import { SplitButtonModel } from '../shared/directives/split-button/split-button.component';
import { IconFontType } from '../shared/icon-font/icon-font-type.enum';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents, KnownReferralPlatform } from '../shared/instrumentation/instrumentation.service';
import { UserProfileService } from '../shared/instrumentation/user-profile.service';
import { ContextMenuItem } from '../shared/models/context-menu-item.model';
import { FileItem, FileType } from '../shared/models/file-item.model';
import { KnownWorkloadReferralNames } from '../shared/models/known-workload-referral-names.model';
import { ObservableScope } from '../shared/models/observable-scope.enum';
import { SectionLoadState, SectionState } from '../shared/models/section-state.enum';
import { AccessTokenService } from '../shared/services/access-token.service';
import { BrowserPaintMeasurementService } from '../shared/services/browser-paint-measurement.service';
import { ConsumerSkuType } from '../shared/services/consumer-license.service';
import { CreateDocumentResult, CreateDocumentResultType, CreateDocumentService } from '../shared/services/create-document.service';
import { DocumentService } from '../shared/services/document.service';
import { ErrorDialogService } from '../shared/services/error-dialog.service';
import { EventService } from '../shared/services/event.service';
import { FormsService } from '../shared/services/forms.service';
import { OfficeRouteItem, OfficeRouteService } from '../shared/services/office-route.service';
import { OneDriveGraphService } from '../shared/services/onedrive-graph.service';
import { OneNoteConfigService } from '../shared/services/onenote-config.service';
import { OneNoteGraphService } from '../shared/services/onenote-graph.service';
import { OneNoteService } from '../shared/services/onenote.service';
import { PerformanceService } from '../shared/services/performance.service';
import { ThemingService } from '../shared/services/theming.service';
import { LaunchAction, UrlLauncherService } from '../shared/services/url-launcher.service';
import { UserDataStorageService } from '../shared/services/user-data-storage.service';
import { WebPwaService } from '../shared/services/web-pwa.service';
import { WorkloadsService } from '../shared/services/workloads.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfigService } from '../shared/shared-context/consumer-install-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { LocService } from '../shared/shared-context/loc.service';
import { ConsumerUserConsentGroup, UserConsentConfigService } from '../shared/shared-context/user-consent-config.service';
import { UserContextService } from '../shared/shared-context/user-context.service';
import { StartPageBaseComponent, TemplatesFolderType } from '../shared/start/start-page-base.component';
import { TemplateCreationHandler, TemplateCreationHandlerFactory, TemplateFields } from '../shared/templates/template-item/template-creation-handler';
import { TemplateItemAnimationType, TemplateItemComponent } from '../shared/templates/template-item/template-item.component';
import { TemplatesConfigService } from '../shared/templates/templates-config.service';
import { CollectionsTemplatesFolder, EnterpriseTemplatesFolder, TemplatesFolder } from '../shared/templates/templates.model';
import { TemplatesService } from '../shared/templates/templates.service';
import { DimeExperience, UpsellDataService } from '../upsell/upsell-data.service';
import { DeviceDetector } from './../shared/services/device-detector.service';
import { WacStartConfigService, WacStartResources } from './wac-start-config.service';
import { getMoreTemplatesUrl, shouldShowStartPageTemplates } from './wac-start.utility';

import './wac-start.component.css';

export enum WacPageAction {
    OpenCreate1 = 'open_create_1'
}

interface TemplateListState {
    hidden: boolean;
}

@Component({
    selector: 'ohp-wac-start',
    templateUrl: './wac-start.component.html',
    animations: [
        trigger('expandAndCollapseTemplates', [
            transition('* => expandTemplates', useAnimation(expand)),
            transition(':leave', useAnimation(collapse))
        ]),
        trigger('buttonEnterLeave', [
            transition('* => buttonIn', animate('500ms ease-in', keyframes([
                style({ opacity: 0, offset: 0 }),
                style({ opacity: 0, offset: .25 }),
                style({ transform: 'scale(1.03)', offset: .25 }),
                style({ opacity: 1, offset: .6 }),
                style({ transform: 'scale(1)', offset: .6 })
            ]))),
            transition('buttonIn => buttonOut', [
                style({ opacity: 1 }),
                animate('100ms 100ms linear', style({ opacity: 0 }))
            ])
        ]),
        trigger('newHeaderEnterLeave', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('100ms ease-in'), style({ opacity: 1 })
            ])
        ])
    ]
})

export class WacStartComponent extends StartPageBaseComponent implements AfterViewChecked {
    public createPrompt: string;
    public showTemplatesAriaLabel: string;
    public showTemplatesText: string;
    public hideTemplatesAriaLabel: string;
    public hideTemplatesText: string;
    public moreTemplatesAriaLabel: string;
    public moreTemplatesText: string;
    public maxInitialDocs: number;
    public maxTotalDocs: number;
    public collapseTemplates: boolean;
    public splitButtonModel: SplitButtonModel;
    public showCreateSection: boolean = false;
    public templateItemAnimationType: TemplateItemAnimationType;
    public buttonAnimationState: string;
    public templateSectionAnimationState: string;
    public buttonAnimationInProgress: boolean;
    public focusBlankTemplateOnLoad: boolean;
    public templateStateAnnouncement: string;
    public showCreateLocation: boolean;
    public disableTemplates: boolean = false;
    public premiumUpsellTemplateHook: TemplateCreationHandler;
    public premiumTemplateIndex: number;
    public premiumTemplateUpsellClosed: boolean;
    public startPageHeaderText: string;

    public showBackButton: Observable<boolean>;
    public defaultBackRoute: OfficeRouteItem;
    public backButtonId: string;

    public mobileMenu: boolean;

    public featuredDocsEnabled: boolean;

    public menuModel: DropdownMenu;

    public templatesPreloadPath: string | OfficeRoute;

    public groupsEnabled: boolean;

    public sectionStateReady: boolean;

    public formsTemplates: boolean;

    private static readonly TemplateListStateKey = 'TemplateListStateKey';

    private routeSubscription: Subscription;
    private templatesStatePromise: Promise<void>;
    private wacPageActionSubscription: Subscription;
    private startPageCalloutPromise: Promise<boolean>;
    private premiumTemplatesUpsellEnabled: boolean;

    constructor(
        activatedRoute: ActivatedRoute,
        capabilitiesService: CapabilitiesService,
        clientStartupConfigService: ClientStartupConfigService,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        contextService: ContextService,
        private readonly createDocumentService: CreateDocumentService,
        private readonly deviceDetector: DeviceDetector,
        documentService: DocumentService,
        eventService: EventService,
        private readonly featureCalloutService: FeatureCalloutService,
        globalErrorHandler: OfficeHomePageErrorHandler,
        instrumentationService: InstrumentationService,
        locService: LocService,
        officeRouteService: OfficeRouteService,
        oneDriveGraphService: OneDriveGraphService,
        templateCreationHandlerFactory: TemplateCreationHandlerFactory,
        public readonly oneNoteService: OneNoteService,
        public readonly formsService: FormsService,
        templatesConfigService: TemplatesConfigService,
        templatesService: TemplatesService,
        public themingService: ThemingService,
        private readonly userDataStorageService: UserDataStorageService,
        private readonly wacStartConfig: WacStartConfigService,
        workloadsService: WorkloadsService,
        errorDialogService: ErrorDialogService,
        public readonly oneNoteGraphService: OneNoteGraphService,
        private readonly oneNoteConfigService: OneNoteConfigService,
        public readonly urlLauncherService: UrlLauncherService,
        public readonly userContextService: UserContextService,
        accessTokenService: AccessTokenService,
        performanceService: PerformanceService,
        browserPaintMeasurementService: BrowserPaintMeasurementService,
        private readonly webPwaService: WebPwaService,
        private readonly upsellService: UpsellDataService,
        private readonly installConfigService: InstallConfigService,
        private readonly userProfileService: UserProfileService,
        userConsentConfigService: UserConsentConfigService
    ) {
        super(
            activatedRoute,
            capabilitiesService,
            clientStartupConfigService,
            contextService,
            documentService,
            eventService,
            errorDialogService,
            globalErrorHandler,
            instrumentationService,
            locService,
            officeRouteService,
            oneDriveGraphService,
            templateCreationHandlerFactory,
            templatesConfigService,
            templatesService,
            workloadsService,
            wacStartConfig.config.startPageNumTemplatesToShow,
            true, /* createFromFileTemplateTileEnabled */
            performanceService,
            browserPaintMeasurementService,
            accessTokenService,
            userConsentConfigService
        );
    }

    public ngAfterViewChecked(): void {
        if (this.app === FileType.Forms && this.capabilitiesService.capabilities.formsCalloutEnabled) {
            this.addFormsStartPageTeachingUI();
        }
    }

    protected onInit() {
        const config = this.wacStartConfig.config;
        this.groupsEnabled = FileType.Forms === this.app && this.isCommercial;
        this.maxInitialDocs = config.maxInitialDocuments;
        this.maxTotalDocs = config.maxTotalDocuments;
        this.focusBlankTemplateOnLoad = true;
        this.showCreateLocation = this.app !== FileType.Sway && this.app !== FileType.Forms;
        this.featuredDocsEnabled = config.featuredDocumentsEnabled && this.app === FileType.Sway;
        this.sectionStateReady = false;
        this.buttonAnimationState = null;
        this.defaultBackRoute = { route: OfficeRoute.OfficeHome };
        this.backButtonId = 'wac-start-close-button';
        this.startPageHeaderText = this.getTitle();
        // Enable for office standalone pwa, but disable for client standalone pwa
        this.showBackButton = this.webPwaService.isStandalone.pipe(map(isStandalone => isStandalone && this.webPwaService.getManifestFileType() !== this.app));
        if (this.capabilitiesService.capabilities.userConsentCallEnabled && !this.isCommercial) {
            this.userConsentConfigService.getConfig().then(userConsentConfig => {
                this.premiumTemplatesUpsellEnabled = this.templatesConfigService.config.premiumTemplatesUpsellEnabled && userConsentConfig.userConsent === ConsumerUserConsentGroup.CanConsent;
            });
        } else {
            this.premiumTemplatesUpsellEnabled = this.templatesConfigService.config.premiumTemplatesUpsellEnabled;
        }
        TemplateItemComponent.isFirstClick = true;

        if (this.app === FileType.Forms) {
            this.formsService.onInitFormsStartPage();
            this.formsTemplates = true;
        }

        if (!this.creationEnabled) {
            return;
        }
        const resources = config.resources;
        this.createPrompt = resources.StartPageCreatePrompt;
        this.moreTemplatesAriaLabel = this.templatesResources.StartPageMoreTemplatesAriaLabel;
        this.moreTemplatesText = this.templatesResources.StartPageMoreTemplatesText;
        const showTemplates = shouldShowStartPageTemplates(this.app, this.contextService.sharedContext.officeMarket.toLowerCase(), this.templatesConfigService);

        if (!this.capabilitiesService.capabilities.templatesEnabled || FileType.OneNote === this.app || FileType.Stream === this.app ||
            (FileType.Forms === this.app && !this.templatesConfigService.config.templatesFormsEnabled && this.isCommercial) || !showTemplates) {
            this.disableTemplates = true;
            this.showCreateSection = this.shouldShowCreateSection(this.app);
            this.collapseTemplates = true;
        } else {
            this.templatesStatePromise = this.loadTemplateListState().then(() => {
                this.showCreateSection = true;
            });
            this.routeSubscription = this.officeRouteService.subscribeToRouteChangeEnd(event => {
                if (event.previousRoute) {
                    const previousRoute = event.previousRoute.route;
                    if (previousRoute === OfficeRoute.OfficeHome) {
                        this.templateItemAnimationType = TemplateItemAnimationType.Slow;
                    } else if (previousRoute === OfficeRoute.Templates) {
                        this.templateItemAnimationType = TemplateItemAnimationType.Fast;
                    } else {
                        this.templateItemAnimationType = TemplateItemAnimationType.None;
                    }
                }
            });

            if (this.installConfigService.config.dimeEnabled && (this.app === FileType.Word || this.app === FileType.Excel || this.app === FileType.PowerPoint)) {
                this.eventService.whenEventFired(AngularEvents.convertedToPaid, ObservableScope.OnlyFuture)
                    .then(() => this.templatesService.updateTemplates(this.app))
                    .then(() => {
                        this.premiumTemplateIndex = -1;
                        this.premiumUpsellTemplateHook = null;
                        this.onTemplatesLoaded();
                    });
            }

            this.premiumTemplateIndex = -1;

            this.templatesPreloadPath = OfficeRoute.Templates;

            if (this.enterpriseTemplatesEnabled) {
                this.enterpriseTemplatesLoading = true;
                this.noTemplatesMessage = this.templatesResources.NoTemplatesMessage;

                this.templatesService.getEnterpriseTemplatesLibraries().then(templateLibraries => {
                    const numTemplates = this.wacStartConfig.config.startPageNumTemplatesToShow - 1;
                    const startIndex = templateLibraries.length < numTemplates ? numTemplates - templateLibraries.length : 0;

                    this.templates.splice(startIndex, templateLibraries.length);
                    this.enterpriseTemplatesLibraries = templateLibraries.slice(0, numTemplates);
                    this.enterpriseTemplatesLoading = false;
                    this.onEnterpriseTemplatesLoaded();
                }).catch(() => {
                    this.enterpriseTemplatesError = true;
                    this.enterpriseTemplatesLoading = false;
                    this.onEnterpriseTemplatesLoaded();
                });
            }
        }

        this.mobileMenu = this.app === FileType.Forms && this.deviceDetector.isMobileDevice();
        this.initializeSplitButton(resources);
        this.onLoadWacPageAction();
    }

    protected onDestroy() {
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }
        if (this.wacPageActionSubscription) {
            this.wacPageActionSubscription.unsubscribe();
        }
    }

    // Keeping onInit and onDestroy at the top for consistency with other components
    // tslint:disable-next-line:member-ordering
    public toggleTemplates() {
        this.focusBlankTemplateOnLoad = false;
        this.collapseTemplates = !this.collapseTemplates;
        this.templateSectionAnimationState = this.collapseTemplates ? 'collapseTemplates' : 'expandTemplates';
        this.templateStateAnnouncement = this.collapseTemplates ? this.showTemplatesText : this.hideTemplatesText;
        this.saveTemplateListState();
        this.buttonAnimationState = this.collapseTemplates ? 'buttonIn' : 'buttonOut';

        const params: InstrumentationParams = {};
        params[InstrumentationParam.Action] = this.collapseTemplates ? 'close' : 'open';
        this.instrumentationService.logClickEvent(KnownInstrumentationEvents.TemplatesToggle, 'hide-show-templates', KnownAreas.Templates, null, null, params);
    }

    // Keeping onInit and onDestroy at the top for consistency with other components
    // tslint:disable-next-line:member-ordering
    public navigateToTemplates(elementId: string, templateFolder: TemplatesFolder = null) {
        this.instrumentationService.logClickEvent(KnownInstrumentationEvents.NavigateToTemplatesClick, elementId, KnownAreas.Templates, undefined, undefined, undefined, undefined, undefined, true);

        const locale = this.contextService.sharedContext.officeMarket.toLowerCase();
        const moreTemplatesUrl = getMoreTemplatesUrl(this.app, locale, this.wacStartConfig, this.capabilitiesService, this.clientStartupConfigService, this.userContextService);
        if (moreTemplatesUrl) {
            this.urlLauncherService.openUrl(moreTemplatesUrl, LaunchAction.OpenTemplate, LinkTarget.SameFrame);
        } else {
            const route = {
                route: OfficeRoute.Templates,
                app: this.app
            } as OfficeRouteItem;

            const templateFolderParam = {};
            if (templateFolder && (templateFolder as EnterpriseTemplatesFolder).DriveId) {
                templateFolderParam['driveId'] = (templateFolder as EnterpriseTemplatesFolder).DriveId;
            }

            if (templateFolder && (templateFolder as CollectionsTemplatesFolder).CollectionId) {
                templateFolderParam['collectionId'] = (templateFolder as CollectionsTemplatesFolder).CollectionId;
            }

            this.officeRouteService.navigateTo(route, false, templateFolderParam);
        }
    }

    // Keeping onInit and onDestroy at the top for consistency with other components
    // tslint:disable-next-line:member-ordering
    public buttonAnimationStart() {
        this.buttonAnimationInProgress = true;
    }

    // Keeping onInit and onDestroy at the top for consistency with other components
    // tslint:disable-next-line:member-ordering
    public buttonAnimationEnd(e: AnimationEvent) {
        if (e.toState === this.buttonAnimationState) {
            this.buttonAnimationInProgress = false;
        }
    }

    /**
     * Detects that loading state of the MRU and once the state reaches completion, allows the featuredDoc section and/or group section to be visible to avoid the MRU section being loaded last and causing
     * a jumpy DOM problem.
     * @param sectionState -possible loading states for the MRU
     */
    // Keeping onInit and onDestroy at the top for consistency with other components
    // tslint:disable-next-line:member-ordering
    public onMruStateChange(sectionState: SectionState) {
        this.sectionStateReady = sectionState && sectionState.state !== SectionLoadState.Loading && sectionState.state !== SectionLoadState.Unknown;
    }

    // Keeping onInit and onDestroy at the top for consistency with other components
    // tslint:disable-next-line:member-ordering
    public onLoadWacPageAction() {
        const shouldSetupActionSubscription = this.app === FileType.OneNote || (this.app === FileType.Stream && this.capabilitiesService.capabilities.createStreamPlaylistEnabled);
        if (!shouldSetupActionSubscription) {
            return;
        }
        this.wacPageActionSubscription = this.activatedRoute.queryParams.subscribe(parms => {
            switch (parms['WacPageAction']) {
                  case WacPageAction.OpenCreate1:
                       this.createNewBlank(KnownInstrumentationEvents.WacPageAction, this.app, WacPageAction.OpenCreate1);
                          break;
            }
        });
    }

    protected getTitle() {
        return this.locService.format(this.wacStartConfig.config.resources.StartPageWelcome, FileItem.fileTypeNameFromType(this.app));
    }

    protected onTemplatesLoaded() {
        if (!this.templatesStatePromise) {
            return;
        }

        const isConsumer = this.clientStartupConfigService.config.authType === AuthType.MSA;
        if (isConsumer) {
            this.consumerInstallConfigService.getConfig().then(consumerInstallConfig => {
                const subscriptionStatus = consumerInstallConfig && consumerInstallConfig.currentSkuInfo ? consumerInstallConfig.currentSkuInfo.skuType : ConsumerSkuType.NotDetected;
                const isPaidConsumer = !(subscriptionStatus === ConsumerSkuType.NonActive || subscriptionStatus === ConsumerSkuType.NotSubscribed);
                if (this.premiumTemplatesUpsellEnabled && !isPaidConsumer && this.templates.length > 0) {
                    this.premiumUpsellTemplateHook = this.templateCreationHandlerFactory.createPremiumLinkCreationHandler(this.app);
                    this.premiumTemplateIndex = -1;

                    if (this.installConfigService.config.dimeEnabled) {
                        this.upsellService.warmupDime(DimeExperience.PremiumTemplates);
                    }

                } else if (isPaidConsumer && this.premiumTemplates && this.premiumTemplates.length > 1) {
                    this.templates.splice(3, 2, this.premiumTemplates[0], this.premiumTemplates[1]);
                }
            });
        }

        this.templatesStatePromise.then(() => {
            if (this.collapseTemplates) {
                return;
            }
            let totalTemplates = this.templates;
            if (this.premiumUpsellTemplateHook) {
                totalTemplates = totalTemplates.concat(this.premiumUpsellTemplateHook);
            }
            this.createInstrumentTemplatesImpression([], totalTemplates);
        });
    }

    protected onEnterpriseTemplatesLoaded() {
        this.templatesStatePromise.then(() => {
            if (this.collapseTemplates) {
                return;
            }

            this.createInstrumentTemplatesImpression(this.enterpriseTemplatesLibraries, [], {
                templatesImpression: KnownInstrumentationEvents.EnterpriseTemplatesImpression,
                templatesErrorImpression: KnownInstrumentationEvents.EnterpriseTemplatesErrorImpression,
                templatesFolderType: TemplatesFolderType.EnterpriseTemplateFolder
            });
        });
    }

    private shouldShowCreateSection(fileType: FileType): boolean {
        switch (fileType) {
            case FileType.OneNote:
                return this.oneNoteConfigService.config.enableOneNoteApis;
            case FileType.Stream:
                return this.capabilitiesService.capabilities.createStreamPlaylistEnabled;
            default:
                return true;
        }
    }

    private initializeSplitButton(resources: WacStartResources) {
        let primaryActionFileType: FileType = this.app;
        const secondaryActions: Array<ContextMenuItem> = [];
        if (this.createFromDocumentTemplate) {
            secondaryActions.push({
                title: this.templatesResources.CreateFromDocumentText,
                ariaLabel: this.templatesResources.CreateFromDocumentAriaLabel,
                icon: IconFontType.Upload,
                iconCssClass: 'create-new-header__button__template-icon',
                cssClass: 'create-new-header__button__menu-item',
                executeAction: () => this.createSwayFromDocument(),
                getId: () => 'create-from-document'
            });
        }

        if (!this.disableTemplates) {
            secondaryActions.push({
                title: this.templatesResources.StartPageCreateFromTemplateText,
                ariaLabel: this.templatesResources.StartPageCreateFromTemplateAriaLabel,
                icon: IconFontType.TextDocument,
                iconCssClass: 'create-new-header__button__template-icon',
                cssClass: 'create-new-header__button__menu-item',
                executeAction: () => this.navigateToTemplates('create-from-template'),
                getId: () => 'create-from-template'
            });
        }

        if (this.app === FileType.Forms) {
            if (this.userContextService.user.isEdu) {
                primaryActionFileType = FileType.FormsQuiz;
                const newFormTitle = this.templatesResources[`${TemplateFields.CreateNewBlankText}${FileType[FileType.Forms]}`];
                secondaryActions.push({
                    ariaLabel: newFormTitle,
                    cssClass: 'create-new-header__button__menu-item',
                    executeAction: () => this.createNewBlank(KnownInstrumentationEvents.CreateLinkClick, FileType.Forms),
                    getId: () => 'formsFormSelection',
                    iconCssClass: 'ms-ohp-svg-Icon ms-ohp-Icon ms-ohp-Icon--formsLogo ms-ohp-Icon--formsLogoFill',
                    title: newFormTitle
                });
            } else {
                secondaryActions.push({
                    title: this.locService.CreateMenuLinkFormsQuizTitle,
                    ariaLabel: this.locService.CreateMenuLinkFormsQuizTitle,
                    iconCssClass: 'ms-ohp-svg-Icon ms-ohp-Icon ms-ohp-Icon--formsLogo ms-ohp-Icon--formsLogoFill',
                    cssClass: 'create-new-header__button__menu-item',
                    executeAction: () => this.createNewBlank(KnownInstrumentationEvents.CreateLinkClick, FileType.FormsQuiz),
                    getId: () => 'formsQuizSelection'
                });
            }
        }

        this.splitButtonModel = {
            id: 'create-new',
            area: KnownAreas.Templates,
            primaryAction: {
                title: this.templatesResources.CreateNewBlankText ||
                    this.templatesResources[`${TemplateFields.CreateNewBlankText}${FileType[primaryActionFileType]}`],
                icon: IconFontType.Add,
                iconCssClass: 'create-new-header__button__icon',
                executeAction: () => this.createNewBlank(KnownInstrumentationEvents.CreateLinkClick, primaryActionFileType),
                ariaLabel: this.templatesResources.CreateNewBlankAriaLabel ||
                    this.templatesResources[`${TemplateFields.CreateNewBlankText}${FileType[primaryActionFileType]}`]
            },
            secondaryActions,
            dropdownAriaLabel: resources.StartPageCreateDropdownAriaLabel,
            dropdownTooltip: resources.StartPageCreateDropdownTooltip
        };
    }

    private createSwayFromDocument() {
        const elementId = this.workloadsService.getWorkloadId(FileType.Sway);
        this.instrumentationService.logClickEvent(
            KnownInstrumentationEvents.CreateFromDocumentClick,
            elementId,
            KnownAreas.Templates,
            KnownWorkloadReferralNames.Sway,
            KnownReferralPlatform.Web);

        this.createFromDocumentTemplate.handleClick(result => {
            const params: InstrumentationParams = {};
            params[InstrumentationParam.Action] = result.type;
            params[InstrumentationParam.Latency] = result.latency.toString();
            params[InstrumentationParam.ElementId] = elementId;
            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.CreateFromDocumentClickResult, params);
        });
    }

    private createNewBlank(eventName: KnownInstrumentationEvents, fileType: FileType = this.app, elementId?: string): Promise<void> {
        elementId = elementId || this.workloadsService.getWorkloadId(fileType);
        const documentType = FileType[fileType].toLowerCase();
        const logClick = (success: boolean, platform?: KnownReferralPlatform) => {
            const params: InstrumentationParams = {};
            params[InstrumentationParam.DocumentType] = documentType;
            this.instrumentationService.logClickEvent(
                eventName,
                elementId,
                KnownAreas.Templates,
                success ? FileItem.referralFromFileType(fileType) : '',
                platform,
                params
            );
        };

        // Only wait to log click if we open in a new tab, otherwise we risk losing the event
        const logResultWithClick = this.contextService.sharedContext.defaultLinkTarget === LinkTarget.NewTab;
        if (!logResultWithClick) {
            logClick(true, KnownReferralPlatform.Web);
        }

        return this.createDocumentService.createItem(fileType, null, null, (result: CreateDocumentResult) => {
            if (logResultWithClick) {
                logClick(result.type !== CreateDocumentResultType.Error, result.platform);
            }
            const params: InstrumentationParams = {};
            params[InstrumentationParam.Action] = result.type;
            params[InstrumentationParam.Latency] = result.latency.toString();
            params[InstrumentationParam.ElementId] = elementId;
            params[InstrumentationParam.Area] = KnownAreas.Templates;
            params[InstrumentationParam.DocumentType] = documentType;
            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.CreateLinkClickResult, params);
        });
    }

    private saveTemplateListState() {
        const currentState: TemplateListState = {
            hidden: this.collapseTemplates
        };
        this.userDataStorageService.set<TemplateListState>(WacStartComponent.TemplateListStateKey, currentState);
    }

    private loadTemplateListState(): Promise<void> {
        return this.userDataStorageService
            .get<TemplateListState>(WacStartComponent.TemplateListStateKey, 0)
            .then(roamingTemplateState => {
                if (!roamingTemplateState) {
                    this.collapseTemplates = false;
                    this.buttonAnimationState = 'buttonOut';
                    this.templateStateAnnouncement = this.hideTemplatesText;
                    return;
                }

                this.collapseTemplates = roamingTemplateState.hidden;
                this.templateStateAnnouncement = this.collapseTemplates ? this.showTemplatesText : this.hideTemplatesText;
                this.buttonAnimationState = this.collapseTemplates ? 'buttonIn' : 'buttonOut';
            })
            .catch(error => {
                // Pass caught error to global handler to ensure that it is properly logged in case it is code error
                this.globalErrorHandler.handleError(error, ErrorId.jZq3J);
            });
    }

    private addFormsStartPageTeachingUI() {
        const recentFormsAnchor = this.documentService.nativeDocument.getElementById('tab-list-header-link-unpinned-mru');
        const groupsContentAnchor = this.documentService.nativeDocument.getElementById('section-header-title');
        if ((!recentFormsAnchor || this.groupsEnabled && !groupsContentAnchor) || this.startPageCalloutPromise) {
            return;
        }

        this.startPageCalloutPromise = this.userProfileService.doesMetricExist(KnownInstrumentationEvents.FormsFeatureCallout, true);
        this.startPageCalloutPromise
            .then(sawFormsFeatureCallout => {
                if (sawFormsFeatureCallout) {
                    return;
                }
                const allMyFormsAnchor = this.documentService.nativeDocument.getElementById('attribution-unpinned-mru');

                let calloutHorizontalOffset = 0;
                if (this.capabilitiesService.capabilities.leftNavReact) {
                    calloutHorizontalOffset = -52; // The width of left nav bar is 52, which align with the value used in feature-callout.component.ts
                }
                const inlineCalloutVerticalOffset = 38; // The offset to move up the arrow and move down the dialog.
                const nonInlineCalloutVerticalOffset = -20; // The distance between the arrow and the content is too long, reduce 20px to make them more align.

                if (recentFormsAnchor) {
                    this.featureCalloutService.addCallout(new FeatureCallout(recentFormsAnchor, CalloutType.FormsFeatureCallout, KnownAreas.Recommended, null,
                        this.locService.FormsCalloutMRU, null, null, 'callout-unpinned-mru', false, false, true, null, false, null, calloutHorizontalOffset, inlineCalloutVerticalOffset, null, -inlineCalloutVerticalOffset));
                }
                if (allMyFormsAnchor) {
                    this.featureCalloutService.addCallout(new FeatureCallout(allMyFormsAnchor, CalloutType.FormsFeatureCallout, KnownAreas.AllMyDocuments, null,
                        this.locService.FormsCalloutMyForms, null, null, 'all-my-forms', false, false, false, null, false, null, calloutHorizontalOffset, nonInlineCalloutVerticalOffset));
                }
                if (groupsContentAnchor) {
                    this.featureCalloutService.addCallout(new FeatureCallout(groupsContentAnchor, CalloutType.FormsFeatureCallout, KnownAreas.FormsGroups, null,
                        this.locService.FormsCalloutGroupForms, null, null, 'my-groups', false, false, true, null, false, null, calloutHorizontalOffset));
                }
                this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.FormsFeatureCallout, {}, [KnownInstrumentationEvents.FormsFeatureCallout]);
            });
    }
}
